import { registerCodes } from '@norkron/web-errors';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { createContext, forwardRef, isValidElement, cloneElement, useRef, useState, useCallback, useEffect, useContext, useId, useMemo } from 'react';
import { ErrorBoundary, DefaultErrorFallback } from '@norkron/web-error-boundary';
import { useFocusTrap, useFocusRestore } from '@norkron/web-a11y';
import { useDismiss } from '@norkron/web-dismiss';
import { useScrollLock, Portal } from '@norkron/web-portal';
import { useControlledState } from '@norkron/web-controlled-state';
import { withConformance, registerResponsiveContract } from '@norkron/web-conformance';
import { jsx, jsxs } from 'react/jsx-runtime';

// src/error-codes.ts
var WEB_DRAWER_LIB_ID = "@norkron/web-drawer";
try {
  registerCodes(WEB_DRAWER_LIB_ID, {
    WEB_DRAWER_OVERLAY_CONTRAST_INSUFFICIENT: {
      description: "selfCheck: the modal scrim/overlay behind an open drawer renders at insufficient opacity (or no background-color) to visually separate the drawer surface from page content. (Conformance severity: major.)",
      severity: "warning",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-drawer/WEB_DRAWER_OVERLAY_CONTRAST_INSUFFICIENT",
      exampleFix: "Apply a scrim with at least `rgba(0,0,0,0.32)` (or the themed `--norkron-overlay` token) on the drawer backdrop element."
    },
    WEB_DRAWER_NO_ESCAPE_DISMISS: {
      description: "selfCheck: an open drawer does not dismiss on `Escape` key \u2014 required by WAI-ARIA dialog/drawer patterns so keyboard users can exit without trapping focus. (Conformance severity: major.)",
      severity: "warning",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-drawer/WEB_DRAWER_NO_ESCAPE_DISMISS",
      exampleFix: 'Wire an Escape-key handler on the drawer root that calls the `onDismiss` / `onClose` prop. If dismissal is deliberately blocked (e.g. an unsaved-changes guard), set `aria-modal="true"` AND surface an in-content cancel button.'
    },
    WEB_DRAWER_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck: an internal error was caught and surfaced as a non-throwing finding (selfCheck never throws). Indicates a defect in the selfCheck itself, not in consumer code. (Conformance severity: info.)",
      severity: "info",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-drawer/WEB_DRAWER_SELFCHECK_INTERNAL_ERROR",
      exampleFix: "File an issue against @norkron/web-drawer with the finding.message stack context."
    }
  });
} catch {
}

// src/self-check.ts
var LIB_ID = "@norkron/web-drawer";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function parseAlpha(color) {
  if (!color) return 1;
  const trimmed = color.trim().toLowerCase();
  if (trimmed === "transparent" || trimmed === "rgba(0, 0, 0, 0)") return 0;
  const rgba = trimmed.match(/rgba?\(([^)]+)\)/);
  if (rgba) {
    const inner = rgba[1];
    if (!inner) return 1;
    const parts = inner.split(",").map((s) => s.trim());
    if (parts.length === 4) {
      const a = Number(parts[3]);
      return Number.isNaN(a) ? 1 : a;
    }
    return 1;
  }
  return 1;
}
function selfCheck(root, opts) {
  const safeOpts = {
    budgetMs: opts?.budgetMs ?? 50,
    minSeverity: opts?.minSeverity ?? "info",
    locale: opts?.locale ?? "en"
  };
  const findings = [];
  try {
    const overlays = Array.from(root.querySelectorAll(".drawer__overlay"));
    const canComputeStyle = typeof window !== "undefined" && typeof window.getComputedStyle === "function";
    for (const overlay of overlays) {
      if (!canComputeStyle) break;
      const style = window.getComputedStyle(overlay);
      const bg = style.backgroundColor;
      const alpha = parseAlpha(bg);
      const hasFilter = style.backdropFilter && style.backdropFilter !== "none" && style.backdropFilter !== "";
      if (alpha < 0.1 && !hasFilter) {
        findings.push({
          libId: LIB_ID,
          code: "WEB_DRAWER_OVERLAY_CONTRAST_INSUFFICIENT",
          category: "a11y",
          severity: "major",
          message: "Drawer overlay has effectively transparent background and no backdrop-filter \u2014 the modal scrim is invisible. Users cannot perceive the dim layer separating modal content from page chrome.",
          selector: selectorFor(overlay),
          suggestedFix: "Set `--drawer-overlay-bg` to a translucent color (e.g. rgba(0,0,0,0.4)) via web-themer, or define `backdrop-filter: blur(...)` on `.drawer__overlay`.",
          extras: { backgroundColor: bg, alpha }
        });
      }
    }
    const openContents = Array.from(
      root.querySelectorAll(
        '.drawer__content[data-state="open"], [role="dialog"][data-state="open"].drawer__content'
      )
    );
    for (const content of openContents) {
      const ariaModal = content.getAttribute("aria-modal");
      const explicitOptOut = content.getAttribute("data-close-on-escape") === "false";
      if (explicitOptOut) {
        findings.push({
          libId: LIB_ID,
          code: "WEB_DRAWER_NO_ESCAPE_DISMISS",
          category: "a11y",
          severity: "major",
          message: "Drawer is open with `closeOnEscape=false`. Keyboard users (and screen-reader users) cannot dismiss the drawer via Escape; provide a visible close affordance and re-enable escape dismissal if possible.",
          selector: selectorFor(content),
          suggestedFix: "Remove `closeOnEscape={false}` unless a focus-trapped close button is guaranteed visible; add a <DrawerClose> action."
        });
      } else if (ariaModal !== "true" && ariaModal !== "false") {
        findings.push({
          libId: LIB_ID,
          code: "WEB_DRAWER_NO_ESCAPE_DISMISS",
          category: "a11y",
          severity: "major",
          message: "Open drawer content lacks `aria-modal`. Screen-reader modality cannot be communicated and assistive tech may not announce Escape as the dismissal affordance.",
          selector: selectorFor(content),
          suggestedFix: "Render the drawer via `<DrawerContent>` (which stamps aria-modal) instead of a hand-rolled equivalent."
        });
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "WEB_DRAWER_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}
function useDrawerIds(base) {
  const auto = useId();
  const root = base ?? `web-drawer-${auto}`;
  return {
    contentId: `${root}-content`,
    titleId: `${root}-title`,
    descriptionId: `${root}-description`
  };
}

// src/version.ts
var LIB_VERSION = "0.1.0";
var WEB_DRAWER_LIB = "@norkron/web-drawer";
var DrawerContext = createContext(null);
function useDrawerContext(fn) {
  const ctx = useContext(DrawerContext);
  if (!ctx && typeof console !== "undefined") {
    console.warn(`[@norkron/web-drawer] ${fn} rendered outside <Drawer>`);
  }
  return ctx;
}
function Drawer(props) {
  const {
    defaultOpen = false,
    open: controlled,
    onOpenChange,
    side: sideProp,
    direction,
    modal = true,
    dismissible = true,
    scaleBackground,
    shouldScaleBackground,
    backgroundScale = 0.94,
    nested = false,
    snapPoints,
    defaultSnapPoint,
    id,
    children
  } = props;
  const side = sideProp ?? direction ?? "right";
  const scaleBg = scaleBackground ?? shouldScaleBackground ?? false;
  const parent = useContext(DrawerContext);
  const reasonRef = useRef("programmatic");
  const wrappedOnOpenChange = useCallback(
    (next) => {
      onOpenChange?.(next, { reason: reasonRef.current });
    },
    [onOpenChange]
  );
  const { value: open, setValue: setOpen } = useControlledState(
    controlled,
    defaultOpen,
    wrappedOnOpenChange
  );
  const ids = useDrawerIds(id);
  const triggerRef = useRef(null);
  const resolvedSnapPoints = useMemo(() => snapPoints ?? [], [snapPoints]);
  const initialSnap = Math.min(
    Math.max(defaultSnapPoint ?? (resolvedSnapPoints.length > 0 ? resolvedSnapPoints.length - 1 : 0), 0),
    Math.max(resolvedSnapPoints.length - 1, 0)
  );
  const [activeSnap, setActiveSnap] = useState(initialSnap);
  const show = useCallback(() => {
    reasonRef.current = "programmatic";
    setOpen(true);
  }, [setOpen]);
  const hide = useCallback(() => {
    reasonRef.current = "programmatic";
    setOpen(false);
  }, [setOpen]);
  const dismissWithReason = useCallback(
    (reason) => {
      if (!dismissible && (reason === "escape" || reason === "outside-click" || reason === "swipe")) {
        return;
      }
      reasonRef.current = reason;
      setOpen(false);
    },
    [setOpen, dismissible]
  );
  const [nestedChildOpenCount, setNestedChildOpenCount] = useState(0);
  const registerNestedChild = useCallback(() => {
    setNestedChildOpenCount((n) => n + 1);
    return () => setNestedChildOpenCount((n) => Math.max(0, n - 1));
  }, []);
  const [titleCount, setTitleCount] = useState(0);
  const [descriptionCount, setDescriptionCount] = useState(0);
  const registerTitle = useCallback(() => {
    setTitleCount((n) => n + 1);
    return () => setTitleCount((n) => Math.max(0, n - 1));
  }, []);
  const registerDescription = useCallback(() => {
    setDescriptionCount((n) => n + 1);
    return () => setDescriptionCount((n) => Math.max(0, n - 1));
  }, []);
  useEffect(() => {
    if (!nested || !parent || !open) return;
    const unregister = parent.registerNestedChild();
    return unregister;
  }, [nested, parent, open]);
  useEffect(() => {
    if (!scaleBg || !open || nested) return;
    if (typeof document === "undefined") return;
    const body = document.body;
    const prevTransform = body.style.transform;
    const prevTransition = body.style.transition;
    const prevOrigin = body.style.transformOrigin;
    const prevRadius = body.style.borderRadius;
    const prevOverflow = body.style.overflow;
    const reduce = typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    body.style.transformOrigin = "center top";
    body.style.transition = reduce ? "none" : "transform 220ms cubic-bezier(0.32, 0.72, 0, 1), border-radius 220ms cubic-bezier(0.32, 0.72, 0, 1)";
    body.style.borderRadius = "var(--drawer-bg-radius, 12px)";
    body.style.setProperty("--drawer-bg-scale", String(backgroundScale));
    body.style.setProperty("--drawer-bg-radius", "12px");
    if (!reduce) {
      body.style.transform = `scale(${backgroundScale})`;
    }
    body.setAttribute("data-drawer-bg-scaled", "true");
    return () => {
      body.style.transform = prevTransform;
      body.style.transition = prevTransition;
      body.style.transformOrigin = prevOrigin;
      body.style.borderRadius = prevRadius;
      body.style.overflow = prevOverflow;
      body.removeAttribute("data-drawer-bg-scaled");
    };
  }, [scaleBg, open, nested, backgroundScale]);
  const ctx = {
    open,
    modal,
    side,
    dismissible,
    nested,
    nestedChildOpenCount,
    registerNestedChild,
    contentId: ids.contentId,
    titleId: ids.titleId,
    descriptionId: ids.descriptionId,
    hasTitle: titleCount > 0,
    hasDescription: descriptionCount > 0,
    registerTitle,
    registerDescription,
    triggerRef,
    show,
    hide,
    dismissWithReason,
    snapPoints: resolvedSnapPoints,
    activeSnap,
    setActiveSnap,
    parent
  };
  return /* @__PURE__ */ jsx(DrawerContext.Provider, { value: ctx, children: /* @__PURE__ */ jsx(ErrorBoundary, { fallback: (err, reset) => /* @__PURE__ */ jsx(DefaultErrorFallback, { error: err, reset }), children }) });
}
var DrawerTrigger = forwardRef(
  function DrawerTrigger2(props, ref) {
    const ctx = useDrawerContext("DrawerTrigger");
    const { children, asChild, className } = props;
    const onClick = () => ctx?.show();
    const ariaProps = {
      "aria-haspopup": "dialog",
      ...ctx?.open && ctx?.contentId ? { "aria-controls": ctx.contentId } : {},
      "aria-expanded": ctx?.open ?? false
    };
    if (asChild && isValidElement(children)) {
      const setRef = (node) => {
        if (ctx) ctx.triggerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      };
      const child = children;
      const childProps = child.props;
      const existingOnClick = childProps.onClick;
      const existingClassName = childProps.className ?? "";
      const mergedClassName = ["drawer__trigger", existingClassName, className].filter(Boolean).join(" ");
      return cloneElement(child, {
        ...ariaProps,
        ref: setRef,
        className: mergedClassName,
        onClick: (e) => {
          existingOnClick?.(e);
          onClick();
        }
      });
    }
    const cls = ["drawer__trigger", className].filter(Boolean).join(" ");
    return /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        ref: (node) => {
          if (ctx) ctx.triggerRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        },
        className: cls,
        "data-allow-unwired": "true",
        "data-allow-asymmetric-padding": "true",
        "data-allow-edge-text": "true",
        ...withConformance(WEB_DRAWER_LIB, LIB_VERSION),
        onClick,
        ...ariaProps,
        children
      }
    );
  }
);
function DrawerOverlay(props) {
  const ctx = useDrawerContext("DrawerOverlay");
  if (!ctx?.open || !ctx.modal) return null;
  if (ctx.nested) return null;
  const cls = ["drawer__overlay", props.className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cls,
      "aria-hidden": true,
      "data-keep-clear": "true",
      onClick: (e) => {
        props.onClick?.(e);
        if (!e.defaultPrevented) ctx.dismissWithReason("outside-click");
      }
    }
  );
}
function DrawerHandle(props) {
  const cls = ["drawer__handle", props.className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx("div", { className: cls, role: "presentation", "aria-label": props["aria-label"] ?? "Drag handle" });
}
function axisForSide(side) {
  return side === "left" || side === "right" ? "x" : "y";
}
function closingSignForSide(side) {
  switch (side) {
    case "right":
      return 1;
    // dragging right closes
    case "left":
      return -1;
    case "top":
      return -1;
    case "bottom":
      return 1;
  }
}
var DrawerContent = forwardRef(
  function DrawerContent2(props, ref) {
    const ctx = useDrawerContext("DrawerContent");
    const {
      children,
      className,
      ariaLabel,
      closeOnEscape = true,
      closeOnOutsideClick = true,
      size = "md",
      swipeToDismiss,
      closeThreshold = 0.33,
      velocityThreshold = 500,
      onEscapeKeyDown,
      onPointerDownOutside,
      initialFocus,
      style
    } = props;
    const localRef = useRef(null);
    const [mounted, setMounted] = useState(false);
    const [dragOffset, setDragOffset] = useState(0);
    const [dragging, setDragging] = useState(false);
    const setRef = (node) => {
      localRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };
    useFocusTrap(
      localRef,
      Boolean(ctx?.open && ctx?.modal)
    );
    useFocusRestore(Boolean(ctx?.open));
    const handleDismiss = useCallback(
      (reason) => {
        if (!ctx) return;
        if (reason === "escape-key") {
          let prevented = false;
          const ev = {
            nativeEvent: new KeyboardEvent("keydown", { key: "Escape" }),
            get defaultPrevented() {
              return prevented;
            },
            set defaultPrevented(v) {
              prevented = v;
            },
            preventDefault() {
              prevented = true;
            }
          };
          onEscapeKeyDown?.(ev);
          if (!closeOnEscape) return;
          if (prevented) return;
          ctx.dismissWithReason("escape");
        } else {
          if (!ctx.modal) return;
          if (!closeOnOutsideClick) return;
          let prevented = false;
          const ev = {
            nativeEvent: new MouseEvent("mousedown"),
            target: null,
            get defaultPrevented() {
              return prevented;
            },
            set defaultPrevented(v) {
              prevented = v;
            },
            preventDefault() {
              prevented = true;
            }
          };
          onPointerDownOutside?.(ev);
          if (prevented) return;
          ctx.dismissWithReason("outside-click");
        }
      },
      [ctx, closeOnEscape, closeOnOutsideClick, onEscapeKeyDown, onPointerDownOutside]
    );
    useDismiss({
      enabled: Boolean(ctx?.open),
      onDismiss: handleDismiss,
      refs: [localRef]
    });
    useScrollLock(Boolean(ctx?.open && ctx?.modal));
    useEffect(() => {
      setMounted(true);
    }, []);
    useEffect(() => {
      if (!ctx?.open || !mounted) return;
      const focusTarget = () => {
        const node = initialFocus?.current;
        if (!node) return false;
        try {
          node.focus();
          return document.activeElement === node;
        } catch {
          return false;
        }
      };
      if (focusTarget()) return;
      const id = typeof requestAnimationFrame === "function" ? requestAnimationFrame(() => {
        focusTarget();
      }) : 0;
      return () => {
        if (id && typeof cancelAnimationFrame === "function") cancelAnimationFrame(id);
      };
    }, [ctx?.open, mounted, initialFocus]);
    const side = ctx?.side ?? "right";
    const swipeEnabled = swipeToDismiss ?? side === "bottom";
    const dragStateRef = useRef(null);
    const onPointerDown = (e) => {
      if (!swipeEnabled || !ctx?.open) return;
      const target = e.target;
      if (target.closest('button, input, textarea, select, a, [role="button"]')) return;
      dragStateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startTime: Date.now(),
        lastDelta: 0,
        pointerId: e.pointerId
      };
      setDragging(true);
      try {
        ;
        e.target.setPointerCapture?.(e.pointerId);
      } catch {
      }
    };
    const onPointerMove = (e) => {
      const st = dragStateRef.current;
      if (!st || st.pointerId !== e.pointerId) return;
      const axis = axisForSide(side);
      const raw = axis === "x" ? e.clientX - st.startX : e.clientY - st.startY;
      const sign = closingSignForSide(side);
      const closing = raw * sign;
      const delta = closing > 0 ? closing : 0;
      st.lastDelta = delta;
      setDragOffset(delta);
    };
    const onPointerUp = (e) => {
      const st = dragStateRef.current;
      if (!st || st.pointerId !== e.pointerId) return;
      const elapsed = Math.max(Date.now() - st.startTime, 1);
      const velocity = st.lastDelta / elapsed * 1e3;
      const rect = localRef.current?.getBoundingClientRect();
      const dim = rect ? axisForSide(side) === "x" ? rect.width : rect.height : 0;
      const threshold = dim * closeThreshold;
      dragStateRef.current = null;
      setDragging(false);
      setDragOffset(0);
      const flickMinDelta = Math.max(dim * 0.125, 24);
      const isFlick = velocity > velocityThreshold && st.lastDelta >= flickMinDelta;
      if (st.lastDelta >= threshold || isFlick) {
        ctx?.dismissWithReason("swipe");
      }
    };
    if (!ctx?.open) return null;
    if (!mounted || typeof document === "undefined") return null;
    const scaledBack = ctx.nestedChildOpenCount > 0;
    const cls = [
      "drawer__content",
      `drawer__content--side-${side}`,
      `drawer__content--size-${size}`,
      ctx.modal ? "drawer__content--modal" : "drawer__content--non-modal",
      dragging ? "drawer__content--dragging" : null,
      ctx.nested ? "drawer__content--nested" : null,
      scaledBack ? "drawer__content--scaled-back" : null,
      className
    ].filter(Boolean).join(" ");
    const translateAxis = axisForSide(side) === "x" ? "X" : "Y";
    const translateValue = dragOffset * closingSignForSide(side);
    const dragTransform = dragging ? `translate${translateAxis}(${translateValue}px)` : void 0;
    const scaleBackTransform = scaledBack ? "scale(0.96) translateY(-8px)" : void 0;
    const composedTransform = [scaleBackTransform, dragTransform].filter(Boolean).join(" ") || void 0;
    const transformStyle = composedTransform ? { transform: composedTransform } : void 0;
    const showBackdrop = ctx.modal && !ctx.nested;
    return /* @__PURE__ */ jsxs(Portal, { children: [
      showBackdrop && /* @__PURE__ */ jsx(
        "div",
        {
          className: "drawer__overlay",
          "aria-hidden": true,
          "data-keep-clear": "true",
          onClick: (reactEvent) => {
            let prevented = false;
            const ev = {
              nativeEvent: reactEvent.nativeEvent,
              target: reactEvent.target,
              get defaultPrevented() {
                return prevented;
              },
              set defaultPrevented(v) {
                prevented = v;
              },
              preventDefault() {
                prevented = true;
              }
            };
            onPointerDownOutside?.(ev);
            if (!closeOnOutsideClick) return;
            if (prevented) return;
            ctx.dismissWithReason("outside-click");
          }
        }
      ),
      /* @__PURE__ */ jsx(
        "div",
        {
          ref: setRef,
          role: "dialog",
          "aria-modal": ctx.modal ? "true" : "false",
          "aria-labelledby": ariaLabel || !ctx.hasTitle ? void 0 : ctx.titleId,
          "aria-describedby": ctx.hasDescription ? ctx.descriptionId : void 0,
          "aria-label": ariaLabel,
          id: ctx.contentId,
          className: cls,
          "data-state": ctx.open ? "open" : "closed",
          "data-side": side,
          "data-keep-clear": "true",
          style: { ...transformStyle, ...style },
          onPointerDown,
          onPointerMove,
          onPointerUp,
          onPointerCancel: onPointerUp,
          ...withConformance(WEB_DRAWER_LIB, LIB_VERSION),
          children
        }
      )
    ] });
  }
);
function DrawerHeader(props) {
  const cls = ["drawer__header", props.className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx("header", { className: cls, children: props.children });
}
function DrawerBody(props) {
  const cls = ["drawer__body", props.className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx("div", { className: cls, children: props.children });
}
function DrawerFooter(props) {
  const cls = ["drawer__footer", props.className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx("footer", { className: cls, children: props.children });
}
function DrawerTitle(props) {
  const ctx = useDrawerContext("DrawerTitle");
  const cls = ["drawer__title", props.className].filter(Boolean).join(" ");
  useEffect(() => {
    if (!ctx) return;
    return ctx.registerTitle();
  }, [ctx]);
  return /* @__PURE__ */ jsx("h2", { id: ctx?.titleId, className: cls, children: props.children });
}
function DrawerDescription(props) {
  const ctx = useDrawerContext("DrawerDescription");
  const cls = ["drawer__description", props.className].filter(Boolean).join(" ");
  useEffect(() => {
    if (!ctx) return;
    return ctx.registerDescription();
  }, [ctx]);
  return /* @__PURE__ */ jsx("p", { id: ctx?.descriptionId, className: cls, children: props.children });
}
function DrawerClose(props) {
  const ctx = useDrawerContext("DrawerClose");
  const { children, asChild, className } = props;
  const onClick = () => ctx?.dismissWithReason("close-button");
  if (asChild && isValidElement(children)) {
    const child = children;
    const childProps = child.props;
    const existingOnClick = childProps.onClick;
    return cloneElement(child, {
      onClick: (e) => {
        existingOnClick?.(e);
        onClick();
      }
    });
  }
  const cls = ["drawer__close", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      className: cls,
      "data-allow-asymmetric-padding": "true",
      "data-allow-edge-text": "true",
      onClick,
      children: children ?? "Close"
    }
  );
}
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-drawer",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    xs: "full",
    sm: "full",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);

// src/index.ts
registerLazy(
  "@norkron/web-drawer",
  () => selfCheck
);

export { Drawer, DrawerBody, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHandle, DrawerHeader, DrawerOverlay, DrawerTitle, DrawerTrigger, LIB_VERSION, RESPONSIVE_CONTRACT, WEB_DRAWER_LIB, WEB_DRAWER_LIB_ID, selfCheck, useDrawerIds };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map